import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { Box, Center, chakra, CloseButton, Text, Flex, Button } from "@chakra-ui/react";

import { genBlock } from "@/utils/bem";

import "./guide-highlight.scss";
import classNames from "classnames";

interface GuideHighlightProps {
  show?: boolean;
  onHide?: () => void;
  storageKey: string;
  targetQuerySelector: string;
  children: React.ReactNode;
  tip: React.ReactNode;
  highlightPaddingX?: number;
  highlightPaddingY?: number;
  defaultPosition?: Position;
  className?: string;
  deps?: React.DependencyList;
}

type Position = {
  top: number;
  height: number;
  left: number;
  width: number;
};
const block = genBlock("c-guide-highlight");
const GuideHighlight: React.FC<GuideHighlightProps> = ({
  show = false,
  onHide,
  storageKey,
  targetQuerySelector,
  children,
  tip,
  highlightPaddingX = 5,
  highlightPaddingY = 5,
  defaultPosition,
  className,
  deps = [],
}) => {
  const [position, setPosition] = useState<Position>({
    height: 0,
    left: 0,
    top: 0,
    width: 0,
    ...(defaultPosition ?? {}),
  });

  useEffect(() => {
    if (show) {
      setTimeout(() => {
        const targetEl = document.querySelector(targetQuerySelector);
        if (targetEl) {
          const { left, top, width, height } = targetEl.getBoundingClientRect();
          const targetHeight = height + (highlightPaddingY ?? 0) * 2;
          const targetWidth = width + (highlightPaddingX ?? 0) * 2;
          const targetLeft = left - highlightPaddingX;
          const targetTop = top - highlightPaddingY;
          setPosition({
            width: targetWidth,
            height: targetHeight,
            top: targetTop,
            left: targetLeft,
          });
        }
      }, 100);
    }
  }, [show, ...deps]);

  function handleHide() {
    onHide?.();
    localStorage.setItem(storageKey, "1");
  }

  const positionPixel = useMemo(() => {
    return {
      width: `${position.width}px`,
      height: `${position.height}px`,
      top: `${position.top}px`,
      left: `${position.left}px`,
    };
  }, [position]);

  const atInitialPosition = useMemo(
    () => Object.values(position).every((value) => value === 0),
    [position],
  );

  return show && !atInitialPosition ? (
    <Box className={classNames(className, block())} pos="fixed" inset={0} zIndex={1005}>
      {/* under Overlay */}
      <Box pos="absolute" {...positionPixel} bg="#D9D9D9" borderRadius="8px" zIndex={1}>
        <Box w="100%" h="100%" bg={"rgba(0, 0, 0, 0.4)"}></Box>
      </Box>
      {/* Overlay */}
      <Box
        className={block({ e: "overlay" })}
        onClick={handleHide}
        w="100%"
        h="100%"
        pos="relative"
        zIndex={2}
        bg={"rgba(0, 0, 0, 0.4)"}
      ></Box>
      {/* above Overlay */}
      <Box
        className={block({ e: "wrapper" })}
        pos="fixed"
        zIndex={3}
        left={positionPixel.left}
        top={positionPixel.top}
        display="flex"
      >
        <Center h="fit-content">
          <Center
            w={`${position.width + highlightPaddingX * 2}px`}
            ml={`${highlightPaddingX * -1}px`}
            h={positionPixel.height}
            onClick={handleHide}
          >
            {children}
          </Center>
          <chakra.div className={block({ e: "dot" })} id="gd-guide-dot" ml="-17px">
            <div className={block({ e: "dot-inner-middle" })}>
              <div className={block({ e: "dot-inner" })}></div>
            </div>
          </chakra.div>
          <chakra.div className={block({ e: "line" })}></chakra.div>
        </Center>

        <Flex
          className={block({ e: "card" })}
          p="12px 16px"
          bg="brand.primary"
          flexDir="column"
          pos="relative"
          top="-12px"
          color="#fff"
          fontWeight={500}
          fontSize="14px"
          rounded="4px"
        >
          <CloseButton
            pos="absolute"
            top="4px"
            right="4px"
            w="24px"
            h="24px"
            onClick={handleHide}
          />
          {tip}
          <Flex justifyContent="flex-end">
            <Button
              bg="#fff"
              borderColor="#fff"
              color="text.primary"
              fontWeight={600}
              fontSize="14px"
              rounded="4px"
              mt="10px"
              size="sm"
              w="92px"
              _hover={{
                bg: "#f3f3f3",
                borderColor: "#f3f3f3",
              }}
              _active={{
                bg: "#e3e3e3",
                borderColor: "#e3e3e3",
              }}
              onClick={handleHide}
            >
              我知道了
            </Button>
          </Flex>
        </Flex>
      </Box>
    </Box>
  ) : null;
};

export default observer(GuideHighlight);
